import { FEATURES } from 'shared/constants/features.constants';
import { REGIONS } from 'shared/constants/region.constants';

type FeatureName = keyof typeof FEATURES;
type Region = (typeof REGIONS)[keyof typeof REGIONS];

interface FeatureInfo {
  label: string;
  description: string;
  /** Epic or implementation ticket that introduced the feature */
  ticket?: string;
  /** Only available where the service behind it is deployed */
  regions?: Region[];
}

/** Turned on per customer, for good */
interface SettingFeature extends FeatureInfo {
  kind: 'setting';
}

/** Temporary: the flag goes away once the feature is on for everyone */
interface RolloutFeature extends FeatureInfo {
  kind: 'rollout';
  stage: 'dev' | 'beta';
}

interface GaRolloutFeature extends FeatureInfo {
  kind: 'rollout';
  stage: 'ga';
  removalTicket: string;
}

export type FeatureMeta = SettingFeature | RolloutFeature | GaRolloutFeature;

// Keyed by constant name so a flag added to FEATURES without an entry here fails typecheck
const FEATURE_META: Record<FeatureName, FeatureMeta> = {
  // Customer settings
  ROTATIONS: {
    kind: 'setting',
    label: 'Rotations',
    description: 'Rotations page and home widget, for customers with trainee programs',
  },
  SCHEDULE_ASSIGNMENTS: {
    kind: 'setting',
    label: 'Schedule assignments',
    description: 'Admin "Upload shifts" tool and shift assignments in My Schedule',
  },
  SCHEDULE_EDUCATIONAL_EVENTS: {
    kind: 'setting',
    label: 'Educational events',
    description: 'Educational events in the schedule',
  },
  SCHEDULE_CASES: {
    kind: 'setting',
    label: 'Schedule cases',
    description: 'OR cases shown under their parent assignment in the schedule (mobile)',
  },
  KNOWLEDGE_HUB_ACCESS: {
    kind: 'setting',
    label: '[Hub] Access',
    description: 'Access to knowledge hub content',
  },
  KNOWLEDGE_HUB_SHARED: {
    kind: 'setting',
    label: '[Hub] Share',
    description: "Shares the organization's content to the knowledge hub",
  },
  WORKSPACE_RESTRICTIONS: {
    kind: 'setting',
    label: 'Workspace restrictions',
    description: 'Restrict who can access a workspace (Restrictions tab in the workspace editor)',
  },
  AUTO_EXTRACT_METADATA: {
    kind: 'setting',
    label: 'Auto extract metadata',
    description: 'Automatically extracts keywords and content type for manually uploaded content',
  },
  ALLOW_KNOWLEDGE_TREE_COPY: {
    kind: 'setting',
    label: '[Knowledge] Allow tree copy',
    description:
      'Exposes admin actions to copy knowledge categories and sections, including mirrored references, within the knowledge tree.',
  },
  QUALITY_METRICS: {
    kind: 'setting',
    label: 'Quality metrics',
    description: 'Quality metrics page and dashboard widget',
  },
  QUALITY_METRICS_CASES: {
    kind: 'setting',
    label: '[Quality Metrics] Cases',
    description: 'Case-by-case tab in quality metrics (mobile)',
  },
  POLICYTECH: {
    kind: 'setting',
    label: 'PolicyTech sync',
    description:
      'Enables the PolicyTech auto-upload pipeline for this organization. When off, dropped SFTP zips are skipped without creating an upload session.',
  },
  TERMS_AND_CONDITIONS: {
    kind: 'setting',
    label: 'Terms and conditions',
    description: "Users must accept C8's terms and privacy policy in the profile-completion popup",
  },
  APPROVAL_MARK: {
    kind: 'setting',
    label: 'Approval mark',
    description:
      '"{Org} Approved" banner on knowledge items, and the organization stamp on generated PDFs',
  },
  PROVIDED_BY: {
    kind: 'setting',
    label: 'Provided by',
    description:
      'Says "provided" instead of "vetted" or "validated" wherever content approval is shown: approval banner, item info, owners and content rows',
  },
  PANDA_KNOWLEDGE_HUB: {
    kind: 'setting',
    label: '[Panda] Knowledge hub',
    description:
      'Offers ASA knowledge-hub answers in Panda chat when the local knowledge base has none. Requires hub access; without it the offer never appears.',
  },
  DOCUMENT_EDITOR: {
    kind: 'setting',
    regions: [REGIONS.US],
    label: 'Document editor',
    description:
      'Enables in-app office document editing via OnlyOffice. Currently limited to Excel files. Should be on for every US customer.',
  },
  SEARCH_V2: {
    kind: 'setting',
    regions: [REGIONS.US],
    label: 'Search v2',
    description:
      'Backend-powered search, replacing the legacy client-side search. Should be on for every US customer.',
  },
  AI_ASK_PANDA: {
    kind: 'setting',
    regions: [REGIONS.US],
    label: '[AI] Ask Panda',
    description:
      'Panda, the AI assistant: header button and chat. Should be on for every US customer.',
  },
  PANDA_IN_SEARCH: {
    kind: 'setting',
    regions: [REGIONS.US],
    label: '[Panda] Panda in search',
    description:
      'Shows a Panda answer card in search results. Requires Ask Panda and Panda v2 to be enabled too. Should be on for every US customer.',
  },

  // Enabled for all, pending removal
  AVATARS: {
    kind: 'rollout',
    stage: 'ga',
    removalTicket: 'PLT-5037',
    label: 'Profile photos',
    description: 'Allow users to upload, replace, and remove their profile photo',
  },
  CONTACTS: {
    kind: 'rollout',
    stage: 'ga',
    removalTicket: 'PLT-5037',
    label: 'Contacts',
    description: 'Contacts directory, including contacts in search results',
  },
  SUGGEST_KEYWORDS: {
    kind: 'rollout',
    stage: 'ga',
    removalTicket: 'PLT-5037',
    label: 'Suggest keywords',
    description: 'Lets users suggest keywords on a knowledge item',
  },
  IMPROVED_SEARCH: {
    kind: 'rollout',
    stage: 'ga',
    removalTicket: 'PLT-5037',
    label: 'Improved search',
    description: 'Improved search results view',
  },
  MY_CONTENT: {
    kind: 'rollout',
    stage: 'ga',
    removalTicket: 'PLT-5037',
    label: 'My content',
    description: 'My content page, listing the content a collaborator owns',
  },
  KNOWLEDGE_COMMENTS: {
    kind: 'rollout',
    stage: 'ga',
    removalTicket: 'PLT-5037',
    label: '[Knowledge] Comments',
    description: 'Discussion board (comments and replies) on knowledge items',
  },
  PERMISSIONS_OWNER_RESTRICTION: {
    kind: 'rollout',
    stage: 'ga',
    removalTicket: 'PLT-5037',
    label: '[Permissions] Owner restriction',
    description:
      'Collaborators can only edit or delete content they own. Account admins and workspace managers are not affected',
  },
  PANDA_V2: {
    kind: 'rollout',
    stage: 'ga',
    removalTicket: 'PLT-4676',
    regions: [REGIONS.US],
    label: '[Panda] Access Panda v2',
    description: "Enables the new Panda v2 experience for this organization's users",
  },

  // Beta
  ORGANIZATION_SELECTOR: {
    kind: 'rollout',
    stage: 'beta',
    label: 'Organization selector',
    description:
      'Asks users with more than one organization to pick one at login. Single-org users never see it.',
  },
};

export const FEATURE_LIST = (Object.keys(FEATURES) as FeatureName[]).map(name => ({
  key: FEATURES[name],
  ...FEATURE_META[name],
}));

export type FeatureListItem = (typeof FEATURE_LIST)[number];

interface FeatureSection {
  id: string;
  title: string;
  description: string;
  includes: (feature: FeatureMeta) => boolean;
  /** Sections start collapsed unless set */
  defaultOpen?: boolean;
}

export const FEATURE_SECTIONS: FeatureSection[] = [
  {
    id: 'settings',
    title: 'Customer settings',
    description: 'Turned on per customer.',
    includes: feature => feature.kind === 'setting',
    defaultOpen: true,
  },
  {
    id: 'ga',
    title: 'Enabled for all, pending removal',
    description: "Meant to be on for every customer it's available to. The flag is being removed.",
    includes: feature => feature.kind === 'rollout' && feature.stage === 'ga',
  },
  {
    id: 'beta',
    title: 'Beta',
    description: 'Rolling out to selected customers.',
    includes: feature => feature.kind === 'rollout' && feature.stage === 'beta',
  },
  {
    id: 'dev',
    title: 'In development',
    description:
      'Can only be turned on for internal organizations. Hidden in production unless already on; move a flag to beta to use it there.',
    includes: feature => feature.kind === 'rollout' && feature.stage === 'dev',
  },
];

export const JIRA_BROWSE_URL = 'https://headtotoe-io.atlassian.net/browse';
